import { I18n } from '../../helpers/i18n.mjs';
import { AIInputConfig, FormanConfigLike } from './let-ai-decide.types';

export const SUPPORTED_INPUT_TYPES = [
	'text',
	'email',
	'url',
	'uuid',
	'color',
	'any',
	'number',
	'integer',
	'uinteger',
	'boolean',
	'array',
	'collection',
	'date',
	'select',
	'dynamicCollection',
];
export const COMPLEX_INPUT_TYPES = ['collection', 'array', 'select'];

// Input types that may have special handling but can be mapped to AI inputs with custom configuration
export const CUSTOM_MAPPABLE_INPUT_TYPES = ['filter'];
/**
 * Checks if a config object (or any of its nested/spec/options children) contains unsupported field instructions,
 * such as RPC strings or forbidden nested structures. Used for strict validation of config schemas.
 *
 * @param {any} config - The configuration object or value to check.
 * @param {boolean} [strictCheck=false] - If true, applies strict rules for unsupported fields, used with complex field types.
 * @param {number} [depth=0] - Internal recursion depth counter (do not set manually).
 * @returns {boolean} True if the config or any nested child contains unsupported instructions, otherwise false.
 */
export const hasUnsupportedFieldInstructions = (
	config: FormanConfigLike | string | null | undefined,
	{ strictCheck = false, depth = 0, supportedTypes = SUPPORTED_INPUT_TYPES } = {},
): boolean => {
	const maxDepth = 100;

	if (depth >= maxDepth) {
		console.warn('Scenario tools type validation: Maximum depth reached.');
		return true;
	}

	if (!config) {
		return false;
	}

	if (strictCheck && typeof config === 'string') {
		return true;
	}

	if (typeof config !== 'object') {
		return false;
	}

	if (config.type && !supportedTypes.includes(config.type)) {
		return true;
	}

	if (strictCheck && 'nested' in config) {
		return true;
	}

	const parentProps = ['spec', 'store', 'options', 'nested'] as const;

	return parentProps.some((prop) => {
		if (!(prop in config)) {
			return false;
		}

		if (strictCheck && prop === 'nested') {
			return true;
		}

		const propValue = config[prop];
		if (strictCheck && typeof propValue === 'string') {
			return true;
		}
		if (Array.isArray(propValue)) {
			return propValue.some((item) =>
				hasUnsupportedFieldInstructions(item, { strictCheck, depth: depth + 1, supportedTypes }),
			);
		}

		return hasUnsupportedFieldInstructions(propValue, { strictCheck, depth: depth + 1, supportedTypes });
	});
};

export function sanitizeInputName(name: number | string | null | undefined) {
	if (name === null || name === undefined) {
		return 'input_name';
	}

	// Coerce numbers to a safe string format
	const trimmedName = String(name).trim();

	// If the name is purely numeric or only wrapped in backticks
	const numericRegex = /^`?(\d+)`?$/;
	const numericMatch = numericRegex.exec(trimmedName);
	if (numericMatch) {
		return `input_${numericMatch[1]}`;
	}

	// Unicode Normalization (Transliteration)
	let sanitizedName = trimmedName.normalize('NFD').replaceAll(/[\u0300-\u036F]/g, '');

	// Replace all non-alphanumeric/underscore characters with a single underscore
	sanitizedName = sanitizedName.replaceAll(/\W/g, '_');

	// Clean up the resulting string for readability
	sanitizedName = sanitizedName.replaceAll(/__+/g, '_');

	while (sanitizedName.startsWith('_')) {
		sanitizedName = sanitizedName.slice(1);
	}
	while (sanitizedName.endsWith('_')) {
		sanitizedName = sanitizedName.slice(0, -1);
	}

	if (sanitizedName === '') {
		return 'input_name';
	}

	if (/^\d/.exec(sanitizedName)) {
		return `input_${sanitizedName}`;
	}

	return sanitizedName;
}

export function sanitizePathForVariables(path: string) {
	return path
		.split('.')
		.map((segment) => sanitizeInputName(segment))
		.join('.');
}

function sanitizeConfigObject<T extends FormanConfigLike>(config: T, maxDepth = 100): T {
	if (!config || typeof config !== 'object' || maxDepth <= 0) {
		return config;
	}

	const sanitized = { ...config };

	if ('name' in sanitized) {
		sanitized.name = sanitizeInputName(sanitized.name);
	}

	if (sanitized.spec) {
		if (Array.isArray(sanitized.spec)) {
			sanitized.spec = sanitized.spec.map((item) =>
				typeof item === 'string' ? item : sanitizeConfigObject(item, maxDepth - 1),
			);
		} else if (typeof sanitized.spec === 'object') {
			sanitized.spec = sanitizeConfigObject(sanitized.spec, maxDepth - 1);
		}
	}

	return sanitized;
}

export function sanitizeInputConfigArray<const T extends FormanConfigLike[]>(inputsConfig: T): T {
	return inputsConfig.map((config) => sanitizeConfigObject(config)) as T;
}

export function getAIInput(name: string): AIInputConfig {
	return {
		path: name,
		name: `{{var.input.${name}}}`,
		type: 'text',
		isSystem: true,
		label: I18n.l('aiBlock.yourAgentWillAutoFillThisField'),
		backgroundColor: '#f4eafc',
		color: '#7302d5',
		custom: true,
		draggable: false,
		expand: true,
	};
}
